import React, { useState, useMemo, useCallback } from 'react';
import { ClipboardList, Eye, Printer, X, Filter } from 'lucide-react';
import { useGetListeInscritsQuery, useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import ListeInscritsPreview, { buildListeInscritsHTML, type InscritsGroup, type PageSize } from '../../components/ListeInscrits/ListeInscritsPreview';
import ScrollToTop from '../../components/ui/ScrollToTop';
import { getNiveauStyle, compareNiveaux } from '../../components/ui/NiveauBanner';
import type { Etudiant } from '../../services/etudiantsApi';

type GroupingMode = 'par_pole' | 'par_niveau' | 'par_classe';
type SortOrder = 'nom,prenoms' | 'classe,nom,prenoms' | 'parcours,nom,prenoms';

const SORT_LABELS: Record<SortOrder, string> = {
  'nom,prenoms': 'tri alphabétique',
  'classe,nom,prenoms': 'tri par classe',
  'parcours,nom,prenoms': 'tri par parcours',
};

const ListeInscrits: React.FC = () => {
  // ── Filtres ──
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('par_classe');
  const [sortOrder, setSortOrder] = useState<SortOrder>('nom,prenoms');
  const [includeFI, setIncludeFI] = useState(true);
  const [includeCS, setIncludeCS] = useState(true);
  const [includeDD, setIncludeDD] = useState(true);
  const [filterPole, setFilterPole] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [pageSize, setPageSize] = useState<PageSize>('A4');

  // ── Aperçu ──
  const [previewHTML, setPreviewHTML] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [erreur, setErreur] = useState('');

  // ── API ──
  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  const { data: choix } = useGetChoixFormulaireQuery();
  const anneeActive = annees.find(a => a.est_active);
  const anneeAcademique = anneeActive?.libelle || '2024-2025';

  // Paramètres de requête backend
  const apiParams = useMemo(() => {
    const p: Record<string, string> = { ordering: sortOrder };
    if (filterPole) p.pole = filterPole;
    if (filterNiveau) p.niveau = filterNiveau;
    return p;
  }, [sortOrder, filterPole, filterNiveau]);

  const { data: allStudents = [], isFetching } = useGetListeInscritsQuery(apiParams);

  // ── Filtrage côté client (type_formation) ──
  const filteredStudents = useMemo(() => {
    const allowed: string[] = [];
    if (includeFI) allowed.push('FI');
    if (includeCS) allowed.push('CS');
    if (includeDD) allowed.push('DD');
    return allStudents.filter(s => allowed.includes(s.type_formation || 'FI'));
  }, [allStudents, includeFI, includeCS, includeDD]);

  // ── Groupement ──
  const buildGroups = useCallback((): InscritsGroup[] => {
    const sortFn = (a: Etudiant, b: Etudiant): number => {
      switch (sortOrder) {
        case 'classe,nom,prenoms':
          return (a.classe || '').localeCompare(b.classe || '')
            || a.nom.localeCompare(b.nom)
            || a.prenoms.localeCompare(b.prenoms);
        case 'parcours,nom,prenoms':
          return (a.parcours || '').localeCompare(b.parcours || '')
            || a.nom.localeCompare(b.nom)
            || a.prenoms.localeCompare(b.prenoms);
        default:
          return a.nom.localeCompare(b.nom) || a.prenoms.localeCompare(b.prenoms);
      }
    };

    const sorted = [...filteredStudents].sort(sortFn);

    const groupMap = new Map<string, Etudiant[]>();
    for (const student of sorted) {
      let key: string;
      switch (groupingMode) {
        case 'par_pole': key = student.pole || 'Sans pôle'; break;
        case 'par_niveau': key = student.niveau || 'Sans niveau'; break;
        case 'par_classe': key = student.classe || 'Sans classe'; break;
      }
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(student);
    }

    const groups: InscritsGroup[] = [];
    groupMap.forEach((students, key) => {
      let title: string;
      switch (groupingMode) {
        case 'par_pole': title = `Pôle : <strong>${key}</strong>`; break;
        case 'par_niveau': title = `Niveau : <strong>${key}</strong>`; break;
        case 'par_classe': title = `Classe : <strong>${key}</strong>`; break;
      }
      groups.push({ groupTitle: title, students, effectif: students.length });
    });

    return groups;
  }, [filteredStudents, groupingMode, sortOrder]);

  // ── Impression / Aperçu ──
  const handleAction = useCallback((doPrint: boolean) => {
    setErreur('');
    const groups = buildGroups();
    if (groups.length === 0) {
      setErreur('Aucun étudiant trouvé avec les filtres sélectionnés.');
      return;
    }

    const html = buildListeInscritsHTML(groups, anneeAcademique, SORT_LABELS[sortOrder], pageSize);

    if (doPrint) {
      const w = window.open('', '_blank');
      if (!w) {
        setErreur("Impossible d'ouvrir la fenêtre d'impression. Vérifiez les popups.");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 600);
    } else {
      setPreviewHTML(html);
      setShowPreview(true);
    }
  }, [buildGroups, anneeAcademique, sortOrder, pageSize]);

  // ── Stats résumé ──
  const statsResume = useMemo(() => {
    const byPole: Record<string, number> = {};
    const byNiveau: Record<string, number> = {};
    for (const s of filteredStudents) {
      const pole = s.pole || 'Indéfini';
      const niveau = s.niveau || 'Indéfini';
      byPole[pole] = (byPole[pole] || 0) + 1;
      byNiveau[niveau] = (byNiveau[niveau] || 0) + 1;
    }
    return { total: filteredStudents.length, byPole, byNiveau };
  }, [filteredStudents]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* ── Titre ── */}
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Liste Provisoire des Inscrits</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Impression pour affichage — Année Académique : <strong>{anneeAcademique}</strong>
          </p>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">Configuration</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Regroupement */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Regroupement</label>
            <div className="space-y-1.5">
              {([
                { value: 'par_pole' as GroupingMode, label: 'Par Pôle' },
                { value: 'par_niveau' as GroupingMode, label: 'Par Niveau' },
                { value: 'par_classe' as GroupingMode, label: 'Par Classe' },
              ]).map(opt => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="grouping"
                    checked={groupingMode === opt.value}
                    onChange={() => setGroupingMode(opt.value)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Types de formation */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Types de formation</label>
            <div className="space-y-1.5">
              {[
                { key: 'FI', label: 'Formation Initiale (FI)', state: includeFI, setter: setIncludeFI },
                { key: 'CS', label: 'Cours du Soir (CS)', state: includeCS, setter: setIncludeCS },
                { key: 'DD', label: 'Double Diplôme (DD)', state: includeDD, setter: setIncludeDD },
              ].map(tf => (
                <label key={tf.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tf.state}
                    onChange={e => tf.setter(e.target.checked)}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{tf.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Ordre de tri + Format */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ordre de tri</label>
              <select
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value as SortOrder)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="nom,prenoms">Alphabétique (noms)</option>
                <option value="classe,nom,prenoms">Par classe, puis noms</option>
                <option value="parcours,nom,prenoms">Par parcours, puis noms</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Format papier</label>
              <div className="flex gap-3">
                {(['A4', 'A3'] as PageSize[]).map(size => (
                  <label key={size} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="pageSize"
                      checked={pageSize === size}
                      onChange={() => setPageSize(size)}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{size}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Filtres optionnels */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle (optionnel)</label>
              <select
                value={filterPole}
                onChange={e => setFilterPole(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Tous les pôles</option>
                {(choix?.poles || []).map(p => (
                  <option key={p.code} value={p.code}>{p.label} ({p.count})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau (optionnel)</label>
              <select
                value={filterNiveau}
                onChange={e => setFilterNiveau(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Tous les niveaux</option>
                {(choix?.niveaux || []).map(n => (
                  <option key={n.code} value={n.code}>{n.label} ({n.count})</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Résumé + Actions */}
        <div className="flex flex-wrap items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>
              Total : <strong className="text-gray-900 dark:text-white">{statsResume.total}</strong> étudiants
            </span>
            {Object.entries(statsResume.byPole).map(([pole, count]) => (
              <span key={pole} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {pole} : {count}
              </span>
            ))}
            {Object.entries(statsResume.byNiveau)
              .sort(([a], [b]) => compareNiveaux(a, b))
              .map(([niveau, count]) => {
                const ns = getNiveauStyle(niveau);
                return (
                  <span key={niveau} className={`text-xs px-2 py-1 rounded font-semibold ${ns.badge}`}>
                    {niveau} : {count}
                  </span>
                );
              })}
          </div>

          <div className="flex gap-3 mt-3 sm:mt-0">
            <button
              onClick={() => handleAction(false)}
              disabled={isFetching || filteredStudents.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Eye size={16} /> Aperçu
            </button>
            <button
              onClick={() => handleAction(true)}
              disabled={isFetching || filteredStudents.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Printer size={16} /> Imprimer
            </button>
          </div>
        </div>

        {erreur && (
          <div className="mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">
            {erreur}
          </div>
        )}
      </div>

      {/* ── Chargement ── */}
      {isFetching && (
        <div className="text-center py-8 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          Chargement des étudiants...
        </div>
      )}

      {/* ── Modal Aperçu ── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Aperçu — Liste Provisoire des Inscrits</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAction(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  <Printer size={14} /> Imprimer
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-2">
              <ListeInscritsPreview html={previewHTML} />
            </div>
          </div>
        </div>
      )}

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

export default ListeInscrits;
